import React from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { v4 as uuid } from 'uuid';

import {
  workflowCreateSectionQuestion,
  workflowSetSectionDescription,
  workflowSortFormQuestions,
} from '~/actions/workflow';
import DescriptionTextAreaForm from '~/components/Workflow/shared/DescriptionTextAreaForm';
import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import { useModalSortable } from '~/ui/hooks/modal';
import { toSnakeTrimmed } from '~/utils/strings';
import { coerceName } from '~/utils/workflow';

import Question from './Question';

function Section({
  // connected
  disabled,
  position,
  section,
  workflowCreateSectionQuestion,
  workflowSetSectionDescription,
  workflowSortFormQuestions,
}) {
  const intl = useIntl();

  const [sortableQuestionListModal, showSortableQuestionListModal] =
    useModalSortable({
      confirm: (items) =>
        workflowSortFormQuestions({ sectionId: section.id, items }),
      items: section.questions.map((question) => ({
        value: question,
        label: question.name,
      })),
      text: intl.formatMessage({
        id: 'Section.sortText',
        defaultMessage: 'Sort',
        description: 'Text for sort button',
      }),
      title: intl.formatMessage({
        id: 'Section.sortTitle',
        defaultMessage: 'Sort Question',
        description: 'Title for sort questions modal',
      }),
    });

  const onCreateQuestion = (sectionId) => {
    const data = {
      id: uuid(),
      position: section.questions.length,
      creating: true,
      name: '',
    };
    workflowCreateSectionQuestion({ sectionId, data });
  };

  const coercedSectionName = coerceName(
    section.name,
    intl.formatMessage({
      id: 'Section.defaultSectionName',
      defaultMessage: 'Form Section',
      description: 'Default name for a new form section.',
    }),
    position,
  );
  const sectionName = toSnakeTrimmed(coercedSectionName);

  return (
    <>
      <FlexLayout disabled={disabled} flexDirection="column" space={8}>
        <DescriptionTextAreaForm
          description={section.description}
          id={`workflow--form-section-name=${sectionName}-add-description`}
          noDescriptionMessage={intl.formatMessage({
            id: 'Section.noDescriptionMessage',
            defaultMessage: 'No description provided for this section.',
            description: 'Message shown when section has no description',
          })}
          onUpdate={(description) =>
            workflowSetSectionDescription({
              sectionId: section.id,
              description,
            })
          }
        />
        <FlexLayout flexDirection="column" space={6}>
          {section.questions.map((question) => (
            <Question
              key={question.id}
              question={question}
              sectionId={section.id}
              onReorder={showSortableQuestionListModal}
            />
          ))}
          <Box>
            <Button
              id={`workflow--form-section-name=${sectionName}-add-question`}
              text={intl.formatMessage({
                id: 'Section.addQuestionButton',
                defaultMessage: 'Add Question',
                description: 'Button text to add a new question',
              })}
              onClick={() => onCreateQuestion(section.id)}
            />
          </Box>
        </FlexLayout>
      </FlexLayout>
      {sortableQuestionListModal}
    </>
  );
}

const mapStateToProps = ({ builder, workflow }, { sectionId }) => ({
  disabled: !builder.shouldEnableAllComponents,
  section: workflow.form.sections.find((section) => section.id === sectionId),
});

export default connect(mapStateToProps, {
  workflowCreateSectionQuestion,
  workflowSortFormQuestions,
  workflowSetSectionDescription,
})(Section);
